import React from 'react';
import { Plus, Trash2, Wrench, Package } from 'lucide-react';
import { PartPicker, PartChip } from '../billing/PartPicker';
import { LabourPicker } from '../billing/LabourPicker';
import { fmtINR, inputCls } from '../billing/shared';
import { lineTotal } from './shared';

const newId = () => Math.random().toString(36).slice(2, 12);
export const labourLine = () => ({ id: newId(), kind: 'labour', name: '', description: '', qty: 1, rate: '', gst_rate: 18, hsn_code: '', unit: 'JOB', part_id: '' });
export const partToLine = (p) => ({
  id: newId(), kind: 'part', name: p.name,
  description: [p.brand, p.sku && `Part no. ${p.sku}`].filter(Boolean).join(' · '),
  qty: 1, rate: Number(p.selling_price || 0), gst_rate: Number(p.gst_rate ?? 18),
  hsn_code: p.hsn_code || '', unit: (p.unit || 'nos').toUpperCase(), part_id: p.id,
  _stock: { qty: Number(p.quantity || 0), unit: p.unit },
});

const Num = ({ value, onChange, testid, disabled, className = '' }) => (
  <input type="number" inputMode="decimal" min="0" step="0.01" value={value} disabled={disabled}
    onChange={(e) => onChange(e.target.value)} className={`${inputCls} text-right ${className}`} data-testid={testid} />
);

const Line = ({ it, i, locked, onChange, onDelete }) => (
  <div className="grid grid-cols-12 gap-2 border-t border-slate-200 px-4 py-3" data-testid={`jc-line-${i}`}>
    <div className="col-span-12 md:col-span-5 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${it.kind === 'part' ? 'bg-teal-50 text-teal-700' : 'bg-violet-50 text-violet-700'}`} data-testid={`jc-line-kind-${i}`}>
          {it.kind === 'part' ? <Package size={10} /> : <Wrench size={10} />} {it.kind}
        </span>
        <input value={it.name} disabled={locked} onChange={(e) => onChange({ name: e.target.value })} placeholder={it.kind === 'part' ? 'Part' : 'Labour, e.g. Brake pad fitting'} className={inputCls} data-testid={`jc-line-name-${i}`} />
      </div>
      <input value={it.description || ''} disabled={locked} onChange={(e) => onChange({ description: e.target.value })} placeholder="Notes (optional)" className={`${inputCls} text-xs`} data-testid={`jc-line-desc-${i}`} />
      {!locked && <PartChip item={it} index={i} onUnlink={() => onChange({ part_id: '', _stock: null, kind: 'labour' })} />}
    </div>
    <div className="col-span-4 md:col-span-1"><label className="md:hidden text-[10px] uppercase text-slate-500">Qty</label><Num value={it.qty} disabled={locked} onChange={(v) => onChange({ qty: v })} testid={`jc-line-qty-${i}`} /></div>
    <div className="col-span-5 md:col-span-2"><label className="md:hidden text-[10px] uppercase text-slate-500">Price incl. GST</label><Num value={it.rate} disabled={locked} onChange={(v) => onChange({ rate: v })} testid={`jc-line-rate-${i}`} /></div>
    <div className="col-span-3 md:col-span-1"><label className="md:hidden text-[10px] uppercase text-slate-500">GST %</label><Num value={it.gst_rate} disabled={locked} onChange={(v) => onChange({ gst_rate: v })} testid={`jc-line-gst-${i}`} /></div>
    <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-2">
      <span className="font-semibold tabular-nums text-slate-900" data-testid={`jc-line-amount-${i}`}>₹ {fmtINR(lineTotal(it))}</span>
      {!locked && (
        <button type="button" onClick={onDelete} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label={`Remove line ${i + 1}`} data-testid={`jc-line-del-${i}`}>
          <Trash2 size={15} />
        </button>
      )}
    </div>
  </div>
);

export const JobCardLines = ({ items, onChange, locked, vehicle }) => {
  const setLine = (i, patch) => onChange(items.map((it, x) => (x === i ? { ...it, ...patch } : it)));
  // Chart prices are pre-GST; job card rates include GST. The first job fills the empty labour row.
  const addLabour = (jobs) => {
    const next = items.slice();
    jobs.forEach((j) => {
      const g = j.gst_rate ?? 18;
      const ln = { ...labourLine(), name: j.name, description: j.package ? `Part of ${j.package}` : '', rate: j.price == null ? '' : Math.round(j.price * (1 + g / 100) * 100) / 100, gst_rate: g, hsn_code: j.sac || '', chart_price: j.price };
      const blank = next.findIndex((it) => it.kind === 'labour' && !it.name && !Number(it.rate));
      if (blank >= 0) next[blank] = ln; else next.push(ln);
    });
    onChange(next);
  };
  const total = items.reduce((a, it) => a + lineTotal(it), 0);
  return (
    <div className="overflow-visible rounded-xl border border-slate-200 bg-white" data-testid="jc-lines">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Parts &amp; labour</h2>
          <p className="text-[11px] text-slate-500">Prices include GST. Parts leave the shelf as soon as the card is saved.</p>
        </div>
        {!locked && (
          <div className="flex items-center gap-2">
            <PartPicker onPick={(p) => onChange([...items, partToLine(p)])} testid="jc-add-part-btn" />
            <LabourPicker vehicle={vehicle} onPick={(j) => addLabour([j])} onPickMany={addLabour} />
            <button type="button" onClick={() => onChange([...items, labourLine()])} className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs text-violet-700 hover:bg-violet-100" data-testid="jc-add-labour-btn">
              <Plus size={13} /> Add labour
            </button>
          </div>
        )}
      </div>
      <div className="hidden md:grid grid-cols-12 gap-2 bg-slate-50 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500">
        <span className="col-span-5">Item</span><span className="col-span-1 text-right">Qty</span>
        <span className="col-span-2 text-right">Price incl. GST</span><span className="col-span-1 text-right">GST %</span>
        <span className="col-span-3 text-right pr-10">Amount</span>
      </div>
      {items.length === 0 && <p className="border-t border-slate-200 px-4 py-6 text-center text-sm text-slate-500" data-testid="jc-lines-empty">Add the parts and labour this car needs.</p>}
      {items.map((it, i) => (
        <Line key={it.id || i} it={it} i={i} locked={locked} onChange={(p) => setLine(i, p)} onDelete={() => onChange(items.filter((_, x) => x !== i))} />
      ))}
      <div className="flex justify-end border-t border-slate-200 px-4 py-3 text-sm">
        <span className="text-slate-500 mr-3">Total</span>
        <span className="font-bold tabular-nums text-slate-900" data-testid="jc-total">₹ {fmtINR(total)}</span>
      </div>
    </div>
  );
};

export default JobCardLines;
